import { TFilterValue, TSaveConditionPayload } from "@irohana-dev/components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreateSavedCondition, useUpdateSavedCondition } from "@/api/services/main/saved-condition";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import {
  TFilterDisplayColumn,
  applyColumnSettings,
  buildColumnOptions,
  enforceLockedColumns,
  hasDisplayColumnsChanged,
} from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { TFilterConditionPanelConfig, TFilterConditionPanelState } from "@/components/commons/filter-condition/types";
import { TITLE_REQUIRED_MESSAGE } from "@/constants/common";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { hasInvalidDate } from "@/utils/dayjs";
import { isApiError, showError } from "@/utils/error";
import { toast } from "@/utils/toast";

export const useFilterConditionPanel = <TFilters, TColumn extends TFilterDisplayColumn>(
  config: TFilterConditionPanelConfig<TFilters, TColumn>,
  props: TFilterConditionPanelState<TFilters, TColumn>,
) => {
  const {
    isOpen,
    mode,
    initialFilters,
    initialDisplayColumns,
    appliedConditionName,
    appliedConditionShareScope,
    appliedConditionIsDefault,
    appliedConditionIncludeColumnSettings,
    appliedConditionColumnSettings,
    onClose,
    onApplyFilters,
    onApplyDisplaySettings,
    onConditionSaved,
  } = props;

  const createSavedCondition = useCreateSavedCondition();
  const updateSavedCondition = useUpdateSavedCondition();

  const [value, setValue] = useState<TFilterValue>({});
  const lockColumns = (cols: TColumn[]) => enforceLockedColumns(cols, config.lockedColumnIds ?? []);
  const [columns, setColumns] = useState<TColumn[]>(() => lockColumns(initialDisplayColumns ?? [...config.defaultColumns]));
  const [conditionName, setConditionName] = useState("");
  const [conditionNameError, setConditionNameError] = useState("");
  const [shareScope, setShareScope] = useState<TSavedConditionShareScope>("self");
  const [isDefault, setIsDefault] = useState(false);
  const [includeColumnSettings, setIncludeColumnSettings] = useState(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  const hasSavedColumnSettings = Boolean(appliedConditionIncludeColumnSettings && appliedConditionColumnSettings);

  useEffect(() => {
    if (!isOpen) return;
    const normalized = initialFilters
      ? { ...config.defaultFilters, ...config.normalizeFilters(initialFilters) }
      : { ...config.defaultFilters };
    setValue(config.toFilterValue(normalized as TFilters));
    setConditionName(appliedConditionName ?? "");
    setConditionNameError("");
    setShareScope(appliedConditionShareScope ?? "self");
    setIsDefault(appliedConditionIsDefault ?? false);
    setIncludeColumnSettings(appliedConditionIncludeColumnSettings ?? false);
    const base = initialDisplayColumns ?? [...config.defaultColumns];
    if (appliedConditionIncludeColumnSettings && appliedConditionColumnSettings) {
      setColumns(
        lockColumns(
          applyColumnSettings(base, appliedConditionColumnSettings.order ?? [], appliedConditionColumnSettings.hidden ?? [], config.defaultColumns),
        ),
      );
    } else if (mode === "create") {
      setColumns([...config.defaultColumns]);
    } else if (initialDisplayColumns) {
      setColumns(lockColumns(initialDisplayColumns));
    }
  }, [isOpen]);

  const columnOptions = useMemo(() => buildColumnOptions(columns), [columns]);
  const columnOrder = useMemo(() => columns.map((col) => col.id), [columns]);
  const hiddenColumns = useMemo(() => columns.filter((col) => !col.visible).map((col) => col.id), [columns]);

  const handleColumnSettingsChange = useCallback(
    (order: string[], hidden: string[]) => setColumns((prev) => lockColumns(applyColumnSettings(prev, order, hidden, config.defaultColumns))),
    [config.defaultColumns],
  );

  const currentFilters = useMemo(() => config.fromFilterValue(value), [value]);

  const handleApply = useCallback(() => {
    const range = config.getDateRange(currentFilters);
    if (hasInvalidDate(range.from, range.to)) return;
    onApplyFilters?.(currentFilters);
    onApplyDisplaySettings?.(columns);
    localStorage.setItem(config.columnsStorageKey, JSON.stringify(columns));
    onClose();
  }, [currentFilters, columns, onApplyFilters, onApplyDisplaySettings, onClose]);

  const handleSaveCondition = useCallback(
    async (payload: TSaveConditionPayload) => {
      const range = config.getDateRange(currentFilters);
      if (hasInvalidDate(range.from, range.to)) return;
      const title = payload.name.trim();
      if (!title) {
        setConditionNameError(TITLE_REQUIRED_MESSAGE);
        return;
      }
      const storeColumns = payload.includeColumnSettings || hasSavedColumnSettings;
      const sharing = {
        shareScope: payload.shareScope,
        isDefault: payload.isDefault,
        includeColumnSettings: storeColumns,
        ...(storeColumns ? { columnSettings: { order: columnOrder, hidden: hiddenColumns } } : {}),
      };
      try {
        let savedId: number;
        if (payload.id !== undefined) {
          savedId = Number(payload.id);
          await updateSavedCondition.mutateAsync({
            id: savedId,
            payload: { title, filters: config.stripEmptyFilters(currentFilters), ...sharing },
          });
        } else {
          const created = await createSavedCondition.mutateAsync({
            pageType: config.pageType,
            title,
            filters: config.stripEmptyFilters(currentFilters),
            ...sharing,
          });
          savedId = Number(created.data.id);
        }
        onConditionSaved?.(savedId, title);
        toast.success("条件を保存しました。");
        handleApply();
      } catch (e) {
        if (isApiError(e) && e.code === ERROR_STATUS_CODES.SAVED_CONDITION_TITLE_DUPLICATED) {
          setConditionNameError(e.message);
          return;
        }
        showError(e);
      }
    },
    [
      currentFilters,
      columnOrder,
      hiddenColumns,
      hasSavedColumnSettings,
      createSavedCondition,
      updateSavedCondition,
      onConditionSaved,
      handleApply,
    ],
  );

  const hasDataToClear = useMemo(
    () => Object.keys(value).length > 0 || hasDisplayColumnsChanged(columns, config.defaultColumns),
    [value, columns],
  );

  const displayMode = mode === "edit" && props.appliedConditionId == null ? "create" : mode;

  return {
    displayMode,
    value,
    setValue,
    columns,
    currentFilters,
    columnOptions,
    columnOrder,
    hiddenColumns,
    handleColumnSettingsChange,
    conditionName,
    conditionNameError,
    handleConditionNameChange: (name: string) => {
      setConditionName(name);
      if (conditionNameError) setConditionNameError("");
    },
    shareScope,
    setShareScope,
    isDefault,
    setIsDefault,
    includeColumnSettings,
    setIncludeColumnSettings,
    hasSavedColumnSettings,
    handleApply,
    handleSaveCondition,
    isSaving: createSavedCondition.isPending || updateSavedCondition.isPending,
    hasDataToClear,
    isClearConfirmOpen,
    setIsClearConfirmOpen,
    clearValue: () => {
      setValue({});
      setColumns([...config.defaultColumns]);
      setIsClearConfirmOpen(false);
    },
  };
};
